import { useEffect, useRef, useState } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import type { MatchGroupLine } from '../../../../hooks/useMatchGroups';
import './LineRow.css';

/**
 * #3237 — card-chrome handles are EDGE ANCHORS, never connection
 * endpoints.
 *
 * The named handles below (``title-target`` / ``title-source`` /
 * ``referenz-source`` / ``bestellung-from-gs`` / ``bestellung-source``)
 * exist so MatchGraph can anchor the dashed REF / Bestellung / Abrechnung
 * arrows at a stable point on the card edge. They are 6×6 px and
 * ``opacity: 0``, and — unlike the Waren-line spots — they carry no
 * ``{event_id}:{idx}`` line id, so ``isLineSpotHandle`` can never accept
 * them and a drag that ends on one is guaranteed to be rejected.
 *
 * That was the #3237 bug: they sit at header / Ref-row height on the edge
 * FACING the other document, so the natural "Lieferschein mit Gutschrift
 * verbinden" gesture was steered onto ``title-source → referenz-source``
 * and dead-ended on the ``non_line`` toast. The placeholder handles in
 * ``MatchGraph`` were already guarded; these were missed.
 *
 * All three flags are set deliberately — ``isConnectable`` alone is NOT
 * enough, because ``@xyflow/react``'s ``Handle`` defaults
 * ``isConnectableStart`` / ``isConnectableEnd`` to ``true``
 * INDEPENDENTLY of it (v12 ``HandleComponent`` signature):
 *
 *  * ``isConnectableStart: false`` — ``Handle.onPointerDown`` early-
 *    returns, so no drag can BEGIN here. Without it the guard would rest
 *    entirely on the stylesheet (``.react-flow__handle`` is
 *    ``pointer-events: none`` until the ``connectionindicator`` class
 *    re-enables it) — and vitest runs with ``css: false``, so a CSS
 *    regression there would be invisible to the suite.
 *  * ``isConnectableEnd: false`` — drops the ``connectableend`` class
 *    that ``XYHandle.isValid`` requires to accept a drop.
 *  * ``isConnectable: false`` — drops the ``connectable`` class (the
 *    other half of that same check) and ``connectionindicator``.
 *
 * What this does NOT do: remove the handle from ``getClosestHandle``'s
 * snap search. That walks ``handleBounds``, which is DOM-measured off the
 * ``.source`` / ``.target`` classes and never consults connectability —
 * so a header-height drop still RESOLVES to a chrome handle, it just
 * resolves to an invalid one. Telling the operator what to do about that
 * is the ``chrome_same_group`` toast in ``handleConnect.ts``.
 *
 * Note the interaction between the two halves, because it decides which
 * gesture the toast serves: with ``isConnectableStart: false`` a drag can
 * no longer BEGIN on chrome, so the literal header→header drag from the
 * issue is now inert rather than wrong. The gesture the guidance copy
 * actually catches is the operator starting on the correct Waren-line dot
 * and RELEASING over the other card's header — the far more common miss,
 * and the one the snap geometry still steers into.
 *
 * Pinned by ``__tests__/LineRow.chromeHandles.test.tsx``.
 */
const CHROME_HANDLE_GUARD = {
  isConnectable: false,
  isConnectableStart: false,
  isConnectableEnd: false,
} as const;

export interface TileAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface LineRowNodeData {
  side: 'delivery' | 'settlement';
  docNumber: string;
  date: string;
  supplierName: string;
  buyerName: string;
  lines: MatchGroupLine[];
  // Per-group test id, set on the primary tile per match group
  // (the settlement side when one exists; the delivery side for
  // orphan_delivery groups). One testid per group regardless of
  // pairing — the canary spec's
  // ``[data-testid^="match-group-tile-"]`` selector counts groups.
  groupTestId?: string;
  // Settlement → delivery back-reference. Renders a "Referenz:" subtitle
  // on the GS tile and anchors a dotted arrow back to the referenced LS.
  //
  // ``date`` is optional (#781): on orphan settlements we synthesise the
  // chip from the GS event entity's ``delivery_reference`` attribute,
  // which carries the LS *number* the GS cites (e.g. ``LF20250222``)
  // but has no date because no matching ``delivery_event`` exists. The
  // renderer below omits the `· <date>` separator when ``date`` is
  // absent so the chip reads cleanly: ``Ref: Lieferschein #LF20250222``.
  //
  // #3030 — that attribute is MIXED-SHAPE: a wibill-native Gutschrift cites the
  // whole set of Lieferscheine its invoice settles, so ``docNumber`` may read
  // ``LI-999917/999912, LI-202606/000002``. MatchGraph does the coercion
  // (``deliveryReferences.ts``); this stays a plain string slot.
  referenceTo?: { docNumber: string; date?: string };
  // POS-number chip (#781). Always rendered when the event entity
  // carries a ``pos_reference`` attribute, on BOTH GS and LS tiles —
  // it's a stable per-event identifier the operator uses to cross-
  // reference between sides. Independent of pairing status; surfaces
  // even on orphan tiles.
  posNumber?: string;
  // Liefertermin chip (#839) — LS-side ONLY. Surfaces the delivery
  // target date that the harvest-delivery-note prints on its
  // ``Liefertermin: <date>`` line, distinct from the document/issue
  // date that the header slot now carries (matching GS / Bestellung
  // conventions). MatchGraph derives this from the delivery_event
  // entity's ``delivery_date`` attribute and only sets it when the
  // value is distinct from the header date — repeating the same
  // date in two slots adds no information for the operator. Omitted
  // on settlement tiles (GS has no Liefertermin concept).
  liefertermin?: string;
  // LS-side BestellNr subtitle (#833). Surfaces the
  // ``delivery_event.bestell_number`` attribute (extracted per #784)
  // as a ``REF: Bestellung #<BestellNr>`` row, mirroring the
  // GS-side ``Ref: Lieferschein #…`` subtitle. Only rendered on the
  // LS (delivery) branch; the matching ``bestellung-source`` Handle
  // is emitted alongside the row so MatchGraph can anchor the
  // Bestellung←LS arrow at the subtitle (instead of routing it from
  // the GS card, which was the pre-#833 fallback that made the LS-
  // side "why this BestellNr?" invisible to the operator).
  bestellNumber?: string;
  // ID of the source document that produced this event — populated from
  // `source_doc_id` on the entity. Used by the page-level preview side
  // panel and surfaced as a "Vorschau" affordance in the tile header.
  sourceDocId?: string | null;
  isActive?: boolean;
  // Render-time hint: when >1, this card stands in for N duplicate entities
  // collapsed at the data layer. Shown as a small "2x" badge by the header.
  duplicateCount?: number;
  // Operator has flagged this orphan as "handled" via the kebab menu.
  // Keeps the tile visible (so the operator can still inspect / revert)
  // but mutes the warning spots and excludes it from the dashboard's
  // "needs attention" counts. Toggleable from the same kebab.
  isResolved?: boolean;
  // #1395 — operator suppressed this erroneous doc via the kebab. Like
  // ``isResolved`` the tile stays on the data-administration canvas so
  // ops can reverse it, but rendered dimmed with a "(ausgeblendet)"
  // marker; it is removed from the dashboard totals AND the
  // farmer-facing feed. Toggleable via the same kebab ("Wieder
  // einblenden").
  isSuppressed?: boolean;
  // #1396 — operator flagged this document via the "markieren &
  // kommentieren" kebab. UNLIKE ``isResolved`` / ``isSuppressed`` this is
  // a NON-destructive annotation: the tile is NEITHER dimmed NOR removed
  // — it only gains a flag marker in the header (and the same flag, with
  // the note, surfaces in the farmer-facing feed). ``flagComment`` is the
  // reviewer's free-text reason, rendered as the marker's tooltip; it may
  // be empty for a bare flag, in which case the marker shows a generic
  // "Markiert" tooltip.
  isFlagged?: boolean;
  flagComment?: string | null;
  // Tile-scoped operator actions surfaced behind a kebab in the header
  // (currently: "Als erledigt markieren" / "Erledigung rückgängig
  // machen" on orphan settlements). Keeps the affordance attached to
  // the document it acts on instead of a standalone footer panel.
  actions?: TileAction[];
  // #844 — keyboard-focus ring driver. ``true`` paints the indigo
  // ``--focused`` box-shadow on this tile so the operator can see
  // where arrow-key navigation has landed. Independent of
  // ``isActive`` so a tile can be focused without yet driving the
  // preview (transient state during multi-key traversal) and vice
  // versa (mouse-click selection leaves keyboard focus untouched
  // until the next arrow press).
  isFocused?: boolean;
  [key: string]: unknown;
}

export const TILE_MENU_CLOSE_EVENT = 'gutschrift-check:close-tile-menus';

function TileActionsMenu({ actions }: { actions: TileAction[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Two complementary close triggers:
  //   1. document.mousedown for clicks anywhere outside the menu's
  //      own DOM subtree (e.g. another tile's body, the page chrome).
  //   2. A custom window event the MatchGraph fires on React Flow's
  //      ``onPaneClick`` — React Flow's pane click handler swallows
  //      the native event before our document listener sees it, so
  //      without this hop a pane click leaves the menu open until
  //      the operator clicks the kebab again. The event also lets
  //      multiple open menus close at once.
  useEffect(() => {
    if (!open) return;
    const onOutsideMouse = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onPaneSignal = () => setOpen(false);
    document.addEventListener('mousedown', onOutsideMouse);
    window.addEventListener(TILE_MENU_CLOSE_EVENT, onPaneSignal);
    return () => {
      document.removeEventListener('mousedown', onOutsideMouse);
      window.removeEventListener(TILE_MENU_CLOSE_EVENT, onPaneSignal);
    };
  }, [open]);

  if (actions.length === 0) return null;
  return (
    <div className="line-row-node__actions" ref={ref}>
      <button
        type="button"
        aria-label="Aktionen"
        aria-haspopup="menu"
        aria-expanded={open}
        className="line-row-node__kebab nodrag"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        ⋮
      </button>
      {open && (
        <div className="line-row-node__menu nodrag" role="menu">
          {actions.map((a, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              className="line-row-node__menu-item"
              disabled={a.disabled}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                a.onClick();
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Categories that represent the actual produce being abgeglichen. Everything
// else (tax/packaging/service/...) is accounting context that gets folded
// into the compact footer — otherwise a GS with 4 USt/Kommission/IFCO rows
// visually crushes a paired LS that only has the one Erdbeeren line.
const PRODUCE_CATEGORIES = new Set(['goods']);

/**
 * Shared subtitle/reference primitive used by every secondary chip row
 * on a tile (#781 ``Ref:`` + ``POS:`` on settlement, #833
 * ``Ref: Bestellung #…`` on delivery). One DOM shape — same 22px row,
 * uppercase muted label, tabular-numeric value — keeps the visual
 * grammar identical across columns so the operator reads "subtitle"
 * the same way regardless of which side it sits on. Extracted into
 * a primitive so #833's LS-side row, the existing GS-side ``Ref:``
 * row, and the cross-side ``POS:`` row all share rendering — a future
 * style nudge propagates to all of them automatically (codex P2 the
 * subtask spec called out as a success criterion).
 */
function RefSubtitle({ label, value }: { label: string; value: string }) {
  return (
    <div className="line-row-node__referenz">
      <span className="line-row-node__referenz-label">{label}</span>
      <span className="line-row-node__referenz-value">{value}</span>
    </div>
  );
}

interface FooterChip {
  label: string;
  amount: number | null;
}

function buildFooter(lines: MatchGroupLine[]): FooterChip[] {
  const chips: FooterChip[] = [];
  for (const l of lines) {
    if (PRODUCE_CATEGORIES.has(l.resourceCategory ?? '')) continue;
    // Prefer the structured rate when present (rendered as "USt 7%" /
    // "Kommission 3,5%"); fall back to the raw product_name which the
    // extractor sometimes already labels.
    let label = l.productName;
    if (l.rate != null && Number.isFinite(l.rate)) {
      const pct = l.rate.toString().replace('.', ',');
      const stem = l.productName.split(/\s+\d/)[0].trim() || l.productName;
      label = `${stem} ${pct}%`;
    }
    chips.push({ label, amount: l.amount });
  }
  return chips;
}

export function LineRowNode({ data }: NodeProps) {
  const d = data as LineRowNodeData;
  const isLeft = d.side === 'delivery';
  const docTypeLabel = isLeft ? 'Lieferschein' : 'Gutschrift';
  const refDocTypeLabel = isLeft ? 'Gutschrift' : 'Lieferschein';

  const produceLines = d.lines.filter((l) =>
    PRODUCE_CATEGORIES.has(l.resourceCategory ?? ''),
  );
  // No category hint at all → assume legacy/LS data where every line is goods.
  // Avoids hiding everything when resource_category isn't populated upstream.
  const everyLineUntagged = d.lines.every((l) => !l.resourceCategory);
  const displayLines = everyLineUntagged ? d.lines : produceLines;
  const footerChips = everyLineUntagged ? [] : buildFooter(d.lines);

  return (
    <div
      className={`line-row-node line-row-node--${d.side}${d.isActive ? ' line-row-node--selected' : ''}${d.isResolved ? ' line-row-node--resolved' : ''}${d.isSuppressed ? ' line-row-node--suppressed' : ''}${d.isFocused ? ' line-row-node--focused' : ''}`}
      data-testid={d.groupTestId}
    >
      <div className="line-row-node__header">
        <span className="line-row-node__doc-number">
          {docTypeLabel} #{d.docNumber}
          {/* #1395 — dim marker so ops can tell at a glance which tile is
              suppressed (and therefore out of the totals + feed). */}
          {d.isSuppressed && (
            <span className="line-row-node__suppressed-badge" title="Aus Abgleich und Übersicht ausgeblendet">
              ausgeblendet
            </span>
          )}
          {/* #1396 — flag marker. NON-destructive: the tile keeps its
              normal styling; the marker just signals the operator (and,
              via the feed, the end-user) singled this document out. The
              reviewer's free-text note is the tooltip — falling back to a
              generic "Markiert" label for a bare flag — so the *why* is
              one hover away on the canvas. */}
          {d.isFlagged && (
            <span
              className="line-row-node__flag-badge"
              title={d.flagComment && d.flagComment.length > 0 ? d.flagComment : 'Markiert'}
              aria-label={d.flagComment && d.flagComment.length > 0 ? `Markiert: ${d.flagComment}` : 'Markiert'}
            >
              ⚑
            </span>
          )}
          {d.duplicateCount && d.duplicateCount > 1 && (
            <span
              className="line-row-node__dup-badge"
              title={`${d.duplicateCount} identische Belege gefunden — eingeklappt zur Übersicht`}
            >
              {d.duplicateCount}×
            </span>
          )}
        </span>
        <span className="line-row-node__date">{d.date}</span>
        {d.actions && d.actions.length > 0 && <TileActionsMenu actions={d.actions} />}
        {/* Target anchor for the dotted reference arrow (LS receives it).
            ``top: 22`` sits at the vertical middle of the LS title row —
            paired with GS REF source at top: 44 plus a REF_ROW_PUSH of
            22 on the LS card, the arrow runs perfectly horizontally
            between the two row middles. */}
        {isLeft && (
          <Handle
            type="target"
            position={Position.Right}
            id="title-target"
            {...CHROME_HANDLE_GUARD}
            style={{ top: 22, right: -4, width: 6, height: 6, opacity: 0, background: 'transparent', border: 'none' }}
          />
        )}
        {/* #902 — GS-side source anchor for the GS→Abrechnung arrow.
            Mirrors the LS ``title-target`` (right edge, title row middle
            at top: 22) so the arrow leaves the GS tile horizontally
            instead of U-turning around it from the left-edge
            ``referenz-source`` handle at top: 44. The ``referenz-source``
            anchor stays — it's still the correct origin for the dotted
            GS→LS reference arrow, which targets the LS column to the
            LEFT of GS. */}
        {!isLeft && (
          <Handle
            type="source"
            position={Position.Right}
            id="title-source"
            {...CHROME_HANDLE_GUARD}
            style={{ top: 22, right: -4, width: 6, height: 6, opacity: 0, background: 'transparent', border: 'none' }}
          />
        )}
        {/* #1119 case 1 — LS-side source anchor for the LS→missing-GS
            arrow in the ``orphan_delivery`` no-token branch. Mirrors
            the GS ``title-source`` above on the LS card (right edge,
            top: 22, same id) so the no-token placeholder receives the
            same dashed reference arrow the typed-token branch already
            emits per LS line — and the ``orphan_settlement`` mirror
            already emits in the opposite direction
            (``MatchGraph.tsx`` ``ref_miss_*``). The shared
            ``title-source`` id is safe across LS / GS because each
            tile renders the handle ONCE (gate flips on ``isLeft``)
            and ReactFlow scopes handle ids per node. The existing
            ``title-target`` on the LS right edge (also at top: 22)
            stays — it's the target-typed anchor for the inbound
            GS→LS REF arrow; the source-typed sibling here adds the
            outbound counterpart. */}
        {isLeft && (
          <Handle
            type="source"
            position={Position.Right}
            id="title-source"
            {...CHROME_HANDLE_GUARD}
            style={{ top: 22, right: -4, width: 6, height: 6, opacity: 0, background: 'transparent', border: 'none' }}
          />
        )}
      </div>
      {/* Source anchor for the dotted reference arrow. Positioned absolutely
          so it sits on the tile's left edge without inserting empty inline
          space inside the flex row — that's what was pushing the label off
          the column where the title sits. ``top: 44`` sits at the vertical
          middle of the GS "Ref:" subtitle row and aligns with the LS
          title-target (top: 22) once MatchGraph shifts the LS card down
          by REF_ROW_PUSH (22px) — keeps the dotted arrow perfectly
          horizontal across the row middles instead of slanted or hugging
          the row tops. */}
      {!isLeft && d.referenceTo && (
        <Handle
          type="source"
          position={Position.Left}
          id="referenz-source"
          {...CHROME_HANDLE_GUARD}
          style={{ position: 'absolute', top: 44, left: -4, width: 6, height: 6, opacity: 0, background: 'transparent', border: 'none' }}
        />
      )}
      {/* #1048 — GS-side source handle for the Bestellung←GS arrow.
          Pre-#1048 this edge sourced ``referenz-source`` too, sharing the
          top: 44 Ref-row anchor with the GS→LS REF arrow. Both arrows
          left the SAME physical handle and both terminated in COL_LEFT,
          so smoothstep routed two parallel L-paths whose elbows landed
          at the same x (= COL_RIGHT - offset = 420) and the dashed
          segments overlapped visually around the bend — the "twisted
          arrows" the issue reports. Splitting the source into a second
          handle at top: 22 (GS title row middle, mirroring
          ``title-source`` on the right edge) gives the two outgoing
          arrows distinct origins; smoothstep then routes them as
          non-overlapping parallel paths. Gated ``!isLeft`` (no
          ``referenceTo`` requirement) so the handle is rendered on EVERY
          GS tile — pickBestellungSourceCard's seCards[0] fallback
          (preference #4) can target a GS without referenceTo, and
          rendering only conditionally would re-introduce the silent
          missing-handle drop pattern #888 fixed. */}
      {!isLeft && (
        <Handle
          type="source"
          position={Position.Left}
          id="bestellung-from-gs"
          {...CHROME_HANDLE_GUARD}
          style={{ position: 'absolute', top: 22, left: -4, width: 6, height: 6, opacity: 0, background: 'transparent', border: 'none' }}
        />
      )}
      {/* #833 / #915 — LS-side source handle for the Bestellung←LS
          arrow. Post-#915 the Bestellung tile stacks ABOVE the LS in
          the same x column (COL_LEFT). Multi-tile groups can have a
          non-adjacent (LS, Bestellung) pair (e.g. two LS each citing
          a different BestellNr), so a straight Top→Bottom edge would
          pass through intervening tiles which React Flow renders
          UNDER nodes (codex round 4 P2). The arrow uses Left→Left
          handles instead, letting smoothstep route the dashed edge
          AROUND the LEFT side of the column (at x ≈ COL_LEFT -
          offset) for any number of stacked tiles. The left-anchored
          handle keeps the visual grammar (arrow originates at the
          ``Ref: Bestellung #…`` subtitle row middle) symmetric with
          the GS-side ``referenz-source`` handle. Emitted only when
          bestellNumber is set — without the row the handle would
          float in empty space and React Flow would still try to
          route an edge there if MatchGraph requested one. */}
      {isLeft && d.bestellNumber && (
        <Handle
          type="source"
          position={Position.Left}
          id="bestellung-source"
          {...CHROME_HANDLE_GUARD}
          style={{ position: 'absolute', top: 44, left: -4, width: 6, height: 6, opacity: 0, background: 'transparent', border: 'none' }}
        />
      )}
      {/* GS-side ``Ref:`` subtitle citing the source Lieferschein. Gated
          ``!isLeft`` to mirror the ``referenz-source`` handle gate above —
          on LS-side, the BestellNr citation (#833) uses its own ``Ref:``
          row, and two consecutive ``Ref:`` labels on one tile would
          confuse the operator. */}
      {!isLeft && d.referenceTo && (
        <RefSubtitle
          label="Ref:"
          value={`${refDocTypeLabel} #${d.referenceTo.docNumber}${
            d.referenceTo.date ? ` · ${d.referenceTo.date}` : ''
          }`}
        />
      )}
      {/* #833 — LS-side ``REF: Bestellung #<BestellNr>`` subtitle.
          Uses the shared primitive so the row reads visually
          identical to the GS ``Ref:`` row above (same uppercase muted
          label via CSS, same 22px height, same tabular-num value).
          Label is the shared ``Ref:`` token; the value carries the
          docTypeLabel (``Bestellung``) so the operator knows which
          column the citation points at without having to follow the
          arrow. */}
      {isLeft && d.bestellNumber && (
        <RefSubtitle label="Ref:" value={`Bestellung #${d.bestellNumber}`} />
      )}
      {/* #839 — Liefertermin chip. LS-side only. Suppressed when the
          value just repeats the header date — no extra information
          for the operator. Sits in the same subtitle stack as Ref /
          POS so the chip styling carries over. */}
      {isLeft && d.liefertermin && d.liefertermin !== d.date && (
        <RefSubtitle label="Liefertermin:" value={d.liefertermin} />
      )}
      {d.posNumber && <RefSubtitle label="POS:" value={d.posNumber} />}
      <div className="line-row-node__orgs">
        <span className="line-row-node__supplier">{d.supplierName}</span>
        {d.buyerName && (
          <>
            <span className="line-row-node__arrow">→</span>
            <span className="line-row-node__buyer">{d.buyerName}</span>
          </>
        )}
      </div>
      <div className="line-row-node__divider" />
      <div className="line-row-node__lines">
        {displayLines.map((line) => {
          // A row "has a connection" only when both halves of the link
          // are populated. ``matched_line_id`` alone (without the
          // ``matched`` status) is treated as unresolved — the spot
          // still shows amber so the reviewer is nudged to look.
          const hasConnection =
            line.matchStatus === 'matched' && line.matchedLineId != null;
          return (
          <div key={line.lineId} className={`line-row-node__line line-row-node__line--${line.matchStatus}`}>
            {/*
              Two stacked elements at the tile edge:
                - the invisible React Flow ``Handle``\s (keep edge
                  endpoints anchored where they always were, so the
                  edge geometry doesn't drift);
                - the visible ``__line-spot`` dot the reviewer reads.
              Splitting them lets us style the dot freely without
              fighting React Flow's own handle styles. They share the
              same ``top: 50%`` anchor so the edge stroke lands on the
              dot centre.

              #1051 — each side now emits BOTH a ``source`` AND a
              ``target`` handle on the same anchor. xyflow v12 allows
              multiple handles sharing the same ``id`` as long as the
              ``type`` differs; the rendered ``sourceHandle``/
              ``targetHandle`` resolution on existing matched edges
              (``sourceHandle: lineId`` on the left card,
              ``targetHandle: lineId`` on the right card) is
              unchanged. The new SECOND handle (``target`` on the
              left, ``source`` on the right) is what lets the user
              drag an edge in either direction (LS→GS or GS→LS) when
              ``nodesConnectable=true`` flips on.
            */}
            {isLeft ? (
              <>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={line.lineId}
                  style={{ position: 'absolute', right: -16, top: '50%', transform: 'translateY(-50%)', width: 9, height: 9, opacity: 0, background: 'transparent', border: 'none' }}
                />
                <Handle
                  type="target"
                  position={Position.Right}
                  id={line.lineId}
                  style={{ position: 'absolute', right: -16, top: '50%', transform: 'translateY(-50%)', width: 9, height: 9, opacity: 0, background: 'transparent', border: 'none' }}
                />
              </>
            ) : (
              <>
                <Handle
                  type="target"
                  position={Position.Left}
                  id={line.lineId}
                  style={{ position: 'absolute', left: -16, top: '50%', transform: 'translateY(-50%)', width: 9, height: 9, opacity: 0, background: 'transparent', border: 'none' }}
                />
                <Handle
                  type="source"
                  position={Position.Left}
                  id={line.lineId}
                  style={{ position: 'absolute', left: -16, top: '50%', transform: 'translateY(-50%)', width: 9, height: 9, opacity: 0, background: 'transparent', border: 'none' }}
                />
              </>
            )}
            <span
              className={`line-row-node__line-spot line-row-node__line-spot--${hasConnection ? 'matched' : 'unmatched'}`}
              aria-hidden="true"
            />

            <span className="line-row-node__line-name">
              <span className="line-row-node__line-name-text">{line.productName}</span>
              {line.packaging && (
                <span
                  className="line-row-node__line-pack"
                  title={`Verpackung: ${line.packaging}`}
                >
                  {line.packaging}
                </span>
              )}
            </span>
            {/*
              Quantity column. Two FRUTANiA shapes in the wild:
                A) Goods row carries `quantity=2772 Kolli` + `pallet_count=33`
                   directly. Both come straight from this line.
                B) Goods row carries `quantity=5 Pal` + `pallet_count=5`, and
                   the Kolli count (146) lives on a sibling packaging line
                   that useMatchGroups folds in via `resolvedKolliCount`.
              Either way the reviewer sees "33 Pal · 2772 Kolli" — the same
              format the PDF prints.
            */}
            {(() => {
              const palCount =
                line.palletCount != null
                  ? line.palletCount
                  : line.quantity != null && (line.quantityUnit ?? '').toLowerCase() === 'pal'
                    ? line.quantity
                    : null;
              const onLineKolli =
                line.quantity != null &&
                line.quantityUnit &&
                line.quantityUnit.toLowerCase() !== 'pal'
                  ? { count: line.quantity, unit: line.quantityUnit }
                  : null;
              const resolvedKolli =
                !onLineKolli && line.resolvedKolliCount != null
                  ? { count: line.resolvedKolliCount, unit: line.resolvedKolliUnit || 'Kolli' }
                  : null;
              const kolli = onLineKolli ?? resolvedKolli;
              if (palCount == null && kolli == null) return null;
              return (
                <span className="line-row-node__line-qty">
                  {palCount != null && <span>{palCount} Pal</span>}
                  {palCount != null && kolli && (
                    <span className="line-row-node__line-qty-sep">·</span>
                  )}
                  {kolli && (
                    <span>
                      {kolli.count} {kolli.unit}
                    </span>
                  )}
                </span>
              );
            })()}
            {line.matchStatus === 'quantity_mismatch' && line.quantityDelta != null && line.quantityDelta !== 0 && (
              <span
                className="line-row-node__line-delta"
                title={`Mengenabweichung: ${line.quantityDelta > 0 ? '+' : ''}${line.quantityDelta} ${line.quantityUnit ?? ''}`}
              >
                Δ {line.quantityDelta > 0 ? '+' : ''}{line.quantityDelta}
              </span>
            )}
          </div>
          );
        })}
        {displayLines.length === 0 && (
          <div className="line-row-node__line line-row-node__line--unmatched">
            <span className="line-row-node__line-name" style={{ color: '#9ca3af', fontStyle: 'italic' }}>
              Keine Warenpositionen
            </span>
          </div>
        )}
      </div>
      {footerChips.length > 0 && (
        <div className="line-row-node__footer" title="Steuern, Verpackung und Gebühren">
          {footerChips.map((chip, i) => (
            <span key={i} className="line-row-node__footer-chip">
              {chip.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Invisible node used as orphan stub edge terminus. Renders ○ or ✕ marker. */
export function StubMarkerNode({ data }: NodeProps) {
  const d = data as { orphanType: 'no_candidate' | 'rejected' };
  return (
    <div className="stub-marker-node">
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
      <span className="stub-marker-node__symbol">
        {d.orphanType === 'rejected' ? '✕' : '○'}
      </span>
    </div>
  );
}
